import { Map } from 'immutable';
import { createSelector, Selector } from '@opengov/compose-app/reselect';
import IntlMessageFormat from 'intl-messageformat';
import IntlRelativeFormat from 'intl-relativeformat';

import { localeSelector } from '../actions';

import { IRelativeFormatSelector, IRelativeFormatSelectorOptions } from '../../types';
import { detectedLocale } from '../../loader/localeLoaderMiddleware';

export const selectCurrentLocale = createSelector(localeSelector, (state) => state.currentLocale);

export const selectIsLocaleLoading = createSelector(localeSelector, (state) =>
  state.currentLocale.namespaces.some((ns) => ns.loading)
);

export const selectLoadingLocaleError = createSelector(localeSelector, (state) =>
  state.currentLocale.namespaces.filter((ns) => ns.error).map((ns) => ns.error)
);

export const selectIsNamespaceLoaded = createSelector(localeSelector, (state) => (namespace) => {
  return !!state.currentLocale.namespaces.has(namespace);
});

export const createRelativeDateFormatSelector = (options?: IRelativeFormatSelectorOptions): IRelativeFormatSelector => {
  return createSelector(selectCurrentLocale, (currentLocale) => {
    if (!currentLocale || !currentLocale.locale) {
      return () => '';
    }

    const msg = new IntlRelativeFormat(currentLocale.locale, options);
    return msg.format;
  });
};

const customFormatters = {
  date: {
    standardShort: { month: '2-digit', day: '2-digit', year: 'numeric' },
    textualDayMonth: { month: 'short', day: '2-digit', year: 'numeric', hour: 'numeric', minute: 'numeric' }
  },
  number: {
    USD: {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    },
    USDNoSymbol: {
      minimumFractionDigits: 2
    },
    percent: {
      style: 'percent',
      maximumFractionDigits: 1
    }
  }
};

export const selectDefaultRelativeDateFormatSelector = createRelativeDateFormatSelector();
const recurseOverTranslations = (currentLocale, namespace, keyPath: string[]) => {
  const isFunction = typeof namespace === 'function';
  const isString = typeof namespace === 'string';

  if (isFunction || isString) {
    // This is some magic to allow us to encode type annotations
    // and the structure of our message namespaces in a data structure
    // but, transform it into a tree of selectors where each selector
    // passes its own key path to retrieve messages from locale data

    // The following code returns a text if your message takes no params ...
    if (isString) {
      try {
        const messageString = currentLocale.namespaces.getIn(keyPath);
        const msg = new IntlMessageFormat(messageString, currentLocale.locale, customFormatters);
        return msg.format();
      } catch (e) {
        return '';
      }
    }

    // or a formatter that takes the same params as defined in your data structure
    // if it's a Function
    if (isFunction) {
      try {
        const messageString = currentLocale.namespaces.getIn(keyPath);
        const msg = new IntlMessageFormat(messageString, currentLocale.locale, customFormatters);
        return msg.format;
      } catch (e) {
        return () => '';
      }
    }

    return namespace;
  }

  return Object.keys(namespace).reduce((result, groupKey) => {
    return {
      ...result,
      [groupKey]: recurseOverTranslations(currentLocale, namespace[groupKey], [...keyPath, groupKey])
    };
  }, namespace);
};

export function getLocaleJson<NS extends {}>(target: NS, currentLocale = detectedLocale) {
  return Object.keys(target).reduce((result, namespaceKey) => {
    return Object.assign(result, {
      [namespaceKey]: recurseOverTranslations(currentLocale, target[namespaceKey], [namespaceKey, 'data'])
    });
  }, {} as { [K in keyof NS]: Map<string, any> });
}

export function processNamespace<NS extends {}>(target: NS): { [K in keyof NS]: (state: Map<string, any>) => NS[K] } {
  return Object.keys(target).reduce((result, namespaceKey) => {
    return Object.assign(result, {
      [namespaceKey]: createSelector(selectCurrentLocale, (currentLocale) => {
        return recurseOverTranslations(currentLocale, target[namespaceKey], [namespaceKey, 'data']);
      })
    });
  }, {} as { [K in keyof NS]: Selector<Map<string, any>, NS[K]> });
}
